import React, { useState, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { colors } from '@constants/colors';
import { getFiltersGlobal } from '@constants/config';
import { Checkbox, PickersUtilsProvider } from '@mui';
import Dialog from '@shared/dialogs/dialog';
import DialogActions from '@shared/dialogs/dialog-actions';
import FormattedDatePicker from '@shared/formatted-date-picker';

/**
 * Custom hook for date range dialog state and logic.
 * @param {object} params - Hook parameters.
 * @returns {object} Hook state and handlers.
 */
export const useDateRangeDialog = ({
  start: startProp,
  end: endProp,
  contain: containProp = false,
  useGlobalDefaults = true
}) => {
  const getDefaultState = useCallback(() => {
    if (useGlobalDefaults && !startProp && !endProp) {
      const { end_date__gte, start_date__lte } = getFiltersGlobal();
      return {
        start: moment(end_date__gte).toDate(),
        end: moment(start_date__lte).toDate(),
        contain: false
      };
    }
    return {
      start: startProp && moment(startProp).toDate(),
      end: endProp && moment(endProp).toDate(),
      contain: containProp
    };
  }, [startProp, endProp, containProp, useGlobalDefaults]);

  const [start, setStart] = useState(() => getDefaultState().start);
  const [end, setEnd] = useState(() => getDefaultState().end);
  const [contain, setContain] = useState(() => getDefaultState().contain);

  const resetToDefault = useCallback(() => {
    const defaultState = getDefaultState();
    setStart(defaultState.start);
    setEnd(defaultState.end);
    setContain(defaultState.contain);
  }, [getDefaultState]);

  const resetToEmpty = useCallback(() => {
    setStart(null);
    setEnd(null);
    setContain(false);
  }, []);

  const handleStartChange = useCallback(newStart => {
    const momentDate = newStart && moment(newStart).toDate();
    setStart(momentDate);
  }, []);

  const handleEndChange = useCallback(newEnd => {
    const momentDate = newEnd && moment(newEnd).toDate();
    setEnd(momentDate);
  }, []);

  const toggleContain = useCallback(() => setContain(prev => !prev), []);

  return {
    start,
    end,
    contain,
    setStart,
    setEnd,
    setContain,
    getDefaultState,
    resetToDefault,
    resetToEmpty,
    handleStartChange,
    handleEndChange,
    toggleContain
  };
};

/**
 * Base date range dialog component with shared UI.
 * @param {object} props - Component props.
 * @returns {JSX.Element|null} The rendered dialog or null.
 */
const BaseDateRangeDialog = ({
  // Dialog state
  isOpen,
  // Date state from hook
  start,
  end,
  contain,
  // Handlers from hook
  handleStartChange,
  handleEndChange,
  toggleContain,
  // Dialog actions
  onCancel,
  onApply,
  // Customization
  dataTypeDisplayName = '',
  showContain = true
}) => {
  const getId = prefix => `${prefix}_picker`;

  const getActions = () => (
    <DialogActions
      onCancel={onCancel}
      onSubmit={onApply}
      submitLabel="APPLY"
    />
  );

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog
      actions={getActions()}
      maxWidth="26rem"
      onClose={onCancel}
      title="Custom date range"
    >
      <PickersUtilsProvider>
        <FormattedDatePicker
          clearable
          hintText="From"
          floatingLabelText="From"
          fullWidth
          value={start}
          id={getId('dialog_from')}
          onChange={handleStartChange}
          maxDate={end || null}
        />
        <FormattedDatePicker
          clearable
          floatingLabelText="To"
          hintText="To"
          fullWidth
          value={end}
          id={getId('dialog_to')}
          onChange={handleEndChange}
          minDate={start || null}
        />
        {showContain && (
          <div style={{ color: colors.opacity.black0_8, paddingTop: '2rem' }}>
            <Checkbox
              checked={contain}
              label={`Show only fully contained ${dataTypeDisplayName}s`}
              onChange={toggleContain}
            />
          </div>
        )}
      </PickersUtilsProvider>
    </Dialog>
  );
};

BaseDateRangeDialog.propTypes = {
  contain: PropTypes.bool,
  dataTypeDisplayName: PropTypes.string,
  end: PropTypes.instanceOf(Date),
  handleEndChange: PropTypes.func.isRequired,
  handleStartChange: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  showContain: PropTypes.bool,
  start: PropTypes.instanceOf(Date),
  toggleContain: PropTypes.func
};

export default memo(BaseDateRangeDialog);
